// frontend/src/pages/PersonaWorkshop/applyCoachProposal.ts
import type { CoachProposal } from '@/types/personaCoach'
import type { BlockCategory } from '@/stores/composerStore'
import type { SkillCatalogEntry } from '@/hooks/useSkillCatalog'
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import type { PersonaBundleResponse, PersonaBundleBlock } from '@/types/personaWizard'
import { focusTargetAcrossSnapshots } from './composedPromptTargets'

// The full BlockCategory union. Previously 9 of 13, which silently
// rejected proposals on never_forget / escalation / do_examples /
// dont_examples as "Unknown block type".
const KNOWN_CATEGORIES: BlockCategory[] = [
  'identity', 'trait', 'tone', 'behavioral', 'constraints',
  'domain', 'output_format', 'examples', 'freeform',
  'never_forget', 'escalation', 'do_examples', 'dont_examples',
]

export interface ProposalContext {
  skills: SkillCatalogEntry[]
  workflows: WorkflowSummary[]
  bundle: PersonaBundleResponse | null
  /** The snapshot the review was generated against (serverBundle). */
  savedBlocks?: PersonaBundleBlock[]
  /** What the focus editor indexes into (draftBundle ?? serverBundle). */
  liveBlocks?: PersonaBundleBlock[]
}

export interface ComposerActions {
  updateBlock: (category: BlockCategory, content: string) => void
  updateBlockAt: (index: number, content: string) => void
  addSkill: (name: string, mode: 'default' | 'auto') => void
  removeSkill: (name: string) => void
  installWorkflow: (workflow_id: string) => void
  removeWorkflow: (workflow_id: string) => void
}

function skillInstalled(bundle: PersonaBundleResponse | null, name: string): boolean {
  if (!bundle) return false
  return bundle.default_skills.some((s) => s.name === name)
    || bundle.auto_skills.some((s) => s.name === name)
}

function workflowInstalled(bundle: PersonaBundleResponse | null, id: string): boolean {
  const list = (bundle?.workflow_bindings ?? []) as Array<{ workflow_id: string }>
  return list.some((w) => w.workflow_id === id)
}

/**
 * Resolves a Review proposal's `bundle_index` into a LIVE draft index.
 *
 * The review describes the SAVED bundle; the store applies to the LIVE
 * one. Those arrays diverge on insert/remove/reorder, so the raw index
 * is meaningless against the draft. Returns null when the block is gone
 * — callers MUST refuse rather than pick a neighbour.
 */
export function resolveLiveIndex(
  p: CoachProposal, ctx: ProposalContext,
): number | null {
  if (p.op !== 'updateBlock' || p.bundle_index === undefined) return null
  const saved = ctx.savedBlocks ?? []
  const live = ctx.liveBlocks ?? saved
  const target = focusTargetAcrossSnapshots(saved, live, p.bundle_index)
  return target?.index ?? null
}

export function validateProposal(
  p: CoachProposal, ctx: ProposalContext,
): { appliable: boolean; reason?: string } {
  switch (p.op) {
    case 'updateBlock': {
      if (!KNOWN_CATEGORIES.includes(p.category)) {
        return { appliable: false, reason: `Unknown block type "${p.category}".` }
      }
      if (p.bundle_index !== undefined && resolveLiveIndex(p, ctx) === null) {
        return {
          appliable: false,
          reason: 'That block no longer exists in your draft — re-run the review.',
        }
      }
      return { appliable: true }
    }
    case 'addSkill':
      return ctx.skills.some((s) => s.name === p.name)
        ? { appliable: true }
        : { appliable: false, reason: `That skill is not available in the catalog.` }
    case 'removeSkill':
      return skillInstalled(ctx.bundle, p.name)
        ? { appliable: true }
        : { appliable: false, reason: `That skill is not currently installed.` }
    case 'installWorkflow':
      return ctx.workflows.some((w) => w.id === p.workflow_id)
        ? { appliable: true }
        : { appliable: false, reason: `That workflow is not available in the catalog.` }
    case 'removeWorkflow':
      return workflowInstalled(ctx.bundle, p.workflow_id)
        ? { appliable: true }
        : { appliable: false, reason: `That workflow is not currently installed.` }
    default:
      return { appliable: false, reason: 'Unsupported proposal.' }
  }
}

export function applyProposal(
  p: CoachProposal, store: ComposerActions, ctx?: ProposalContext,
): void {
  switch (p.op) {
    case 'updateBlock': {
      if (p.bundle_index !== undefined) {
        const live = resolveLiveIndex(p, ctx ?? { skills: [], workflows: [], bundle: null })
        // Refuse rather than retarget — see resolveLiveIndex.
        if (live === null) return
        store.updateBlockAt(live, p.content)
        return
      }
      store.updateBlock(p.category, p.content)
      return
    }
    case 'addSkill': store.addSkill(p.name, p.mode); break
    case 'removeSkill': store.removeSkill(p.name); break
    case 'installWorkflow': store.installWorkflow(p.workflow_id); break
    case 'removeWorkflow': store.removeWorkflow(p.workflow_id); break
  }
}
